import React, { useState, useEffect, useRef } from "react";
import API from "../services/api";
import SignatureCanvas from "react-signature-canvas";
import { todayEastern } from "../dateUtils";

/* ===============================
   MODAL: CREATE NEW SCHOOL NOTE
   =============================== */
function SchoolNoteModal({ isOpen, onClose, onSubmit, clientId, clientName }) {
  // All hooks must be called unconditionally (before any early returns)
  const [date, setDate] = useState(todayEastern());
  const [clientDob, setClientDob] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(todayEastern());
  const [therapistName, setTherapistName] = useState("");
  const [therapistCredentials, setTherapistCredentials] = useState("");
  const [therapistTitle, setTherapistTitle] = useState("Therapist");
  const [therapistPhone, setTherapistPhone] = useState("");
  const [therapistPhoneExtension, setTherapistPhoneExtension] = useState("");
  const [therapistEmail, setTherapistEmail] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [customContent, setCustomContent] = useState(false);
  const [customNoteContent, setCustomNoteContent] = useState("");
  
  // State for signature
  const [signatureData, setSignatureData] = useState(null);
  const signatureRef = useRef(null);
  
  // Fetch client DOB on mount
  useEffect(() => {
    if (clientId && isOpen) {
      const fetchClientDetails = async () => {
        try {
          const res = await API.get(`/therapist/clients/${clientId}`);
          if (res.data && res.data.dob) {
            setClientDob(res.data.dob);
          }
        } catch (err) {
          console.error("Error fetching client details:", err);
        }
      };
      
      fetchClientDetails();
    }
  }, [clientId, isOpen]);
  
  // Fetch therapist details on mount
  useEffect(() => {
    if (isOpen) {
      const fetchTherapistDetails = async () => {
        try {
          const res = await API.get("/therapist/profile");
          if (res.data) {
            // Construct full name from first_name and last_name
            const fullName = [res.data.first_name, res.data.last_name]
              .filter(Boolean)
              .join(' ');
            
            setTherapistName(fullName || "");
            
            // Handle credentials - could be in license_type or a separate field
            setTherapistCredentials(res.data.license_type || res.data.credentials || "");
            
            // Set title if available
            setTherapistTitle(res.data.title || "Therapist");
            
            // Set contact info
            setTherapistPhone(res.data.phone || "");
            setTherapistPhoneExtension(res.data.phone_extension || "");
            setTherapistEmail(res.data.email || "");
          }
        } catch (err) {
          console.error("Error fetching therapist details:", err);
        }
      };
      
      fetchTherapistDetails();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset to default values when modal is closed
      setDate(todayEastern());
      setClientDob("");
      setAppointmentTime("");
      setAppointmentDate(todayEastern());
      setAdditionalNotes("");
      setCustomContent(false);
      setCustomNoteContent("");
      setSignatureData(null);
      if (signatureRef.current) {
        signatureRef.current.clear();
      }
    }
  }, [isOpen]);

  // Early return AFTER all hooks have been called
  if (!isOpen) return null;

  const updateSignature = () => {
    try {
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        setSignatureData(signatureRef.current.toDataURL());
      } else {
        setSignatureData(null);
      }
    } catch (error) {
      console.error('Error updating signature:', error);
    }
  };
  
  const clearSignature = () => {
    try {
      if (signatureRef.current) {
        signatureRef.current.clear();
        setSignatureData(null);
      }
    } catch (error) {
      console.error('Error clearing signature:', error);
    }
  };

  // Format date for display (MM/DD/YYYY)
  // Handles both YYYY-MM-DD and ISO timestamp formats
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    
    try {
      // If it's an ISO timestamp (contains 'T'), extract just the date part
      let dateOnly = dateStr;
      if (dateStr.includes('T')) {
        dateOnly = dateStr.split('T')[0];
      }
      
      // Now split the YYYY-MM-DD format
      const parts = dateOnly.split("-");
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
      
      // Fallback: try to parse as Date object
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      }
      
      return dateStr;
    } catch (error) {
      return dateStr;
    }
  };
  
  // Get day of week - FIXED to handle timezone correctly
  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return "";
    
    try {
      // Split the date string to get year, month, and day
      const parts = dateStr.split("-");
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed in JavaScript
      const day = parseInt(parts[2], 10);
      
      // Create date using local timezone instead of UTC
      const date = new Date(year, month, day);
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return days[date.getDay()];
    } catch (error) {
      console.error("Error getting day of week:", error);
      return "";
    }
  };

  const handleSave = () => {
    if (!signatureData) {
      alert("Please sign the note before saving");
      return;
    }
    
    const schoolNoteObj = {
      type: "SchoolNote",
      date,
      clientName,
      clientDob,
      appointmentTime,
      appointmentDate,
      appointmentDayOfWeek: getDayOfWeek(appointmentDate),
      therapistName,
      therapistCredentials,
      therapistTitle,
      therapistPhone,
      therapistPhoneExtension,
      therapistEmail,
      additionalNotes,
      customContent,
      customNoteContent,
      signatureData
    };
    
    onSubmit(schoolNoteObj);
  };

  const previewNote = () => {
    // Create a preview window
    const previewWin = window.open("", "_blank", "width=800,height=600");
    if (!previewWin) {
      alert("Unable to open preview window. Please check your popup blocker settings.");
      return;
    }
    
    // Get standard or custom content
    let noteContent;
    if (customContent) {
      noteContent = customNoteContent;
    } else {
      noteContent = `This letter is to document ${clientName}'s presence at Avenues Unlimited Counseling Center for ${appointmentTime ? `their ${appointmentTime}` : "their"} session on ${getDayOfWeek(appointmentDate)} ${formatDate(appointmentDate)}.${additionalNotes ? `\n\n${additionalNotes}` : ""}`;
    }
    
    // Write the preview HTML
    previewWin.document.write(`
      <html>
      <head>
        <title>School Note Preview</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.5;
            margin: 40px;
          }
          .letterhead-container {
            text-align: center;
            margin-bottom: 20px;
          }
          .letterhead {
            max-width: 100%;
            height: auto;
          }
          .footer-container {
            text-align: center;
            margin-top: 30px;
          }
          .footer {
            max-width: 100%;
            height: auto;
          }
          .date {
            margin-bottom: 20px;
          }
          .regarding {
            margin-bottom: 30px;
          }
          .content {
            margin-bottom: 30px;
            white-space: pre-line;
          }
          .signature-area {
            margin-top: 40px;
          }
          .signature {
            max-width: 200px;
            margin-bottom: 10px;
          }
          .closing {
            margin-bottom: 10px;
          }
          .contact {
            margin-top: 5px;
            font-size: 0.9em;
          }
        </style>
      </head>
      <body>
        <div class="letterhead-container">
          <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" class="letterhead">
        </div>
        
        <div class="date">${formatDate(date)}</div>
        
        <div class="regarding">
          <p>Re: ${clientName}</p>
          <p>DOB: ${formatDate(clientDob)}</p>
        </div>
        
        <p>To whom it may concern,</p>
        
        <div class="content">
          ${noteContent}
        </div>
        
        <p>If further information is needed, please contact me directly using the information below.</p>
        
        <div class="closing">Sincerely,</div>
        
        <div class="signature-area">
          ${signatureData ? `<img src="${signatureData}" class="signature" alt="Therapist Signature">` : '(No signature)'}
          <p>${therapistName}${therapistCredentials ? `, ${therapistCredentials}` : ""}</p>
          <p>${therapistTitle}</p>
          <p class="contact">p. ${therapistPhone}${therapistPhoneExtension ? ` ext. ${therapistPhoneExtension}` : ""}</p>
          <p class="contact">e. ${therapistEmail}</p>
        </div>
        
        <div class="footer-container">
          <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" class="footer">
        </div>
      </body>
      </html>
    `);
    
    previewWin.document.close();
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>New School Note</h2>
        
        <div style={styles.clientInfo}>
          <strong>Client:</strong> {clientName || "Selected Client"} | <strong>Date:</strong> {formatDate(date)}
          <button 
            onClick={() => setDate(new Date().toISOString().split("T")[0])}
            style={styles.smallButton}
          >
            Set Today
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>
        
        <div style={styles.formScrollArea}>
          {/* Client DOB */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Client Information:</h3>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Client Date of Birth:</label>
              <div style={styles.readOnly}>
                {clientDob ? formatDate(clientDob) : "Not available"}
              </div>
            </div>
          </div>
          
          {/* APPOINTMENT DETAILS */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Appointment Details:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Appointment Date:</label>
              <input
                type="date"
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
                style={styles.input}
              />
              {appointmentDate && (
                <div style={styles.helperText}>
                  Day of week: {getDayOfWeek(appointmentDate)}
                </div>
              )}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Appointment Time/Type (e.g., "morning", "1:00 PM", "after-school"):</label>
              <input
                type="text"
                value={appointmentTime}
                onChange={(e) => setAppointmentTime(e.target.value)}
                style={styles.input}
                placeholder="e.g., morning, 1:00 PM, after-school"
              />
            </div>
          </div>
          
          {/* THERAPIST INFO - Now auto-populated but still editable */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Therapist Information:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Therapist Name:</label>
              <input
                type="text"
                value={therapistName}
                onChange={(e) => setTherapistName(e.target.value)}
                style={styles.input}
                placeholder="Full name"
              />
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Credentials (e.g., LCSW, LPC, PhD):</label>
              <input
                type="text"
                value={therapistCredentials}
                onChange={(e) => setTherapistCredentials(e.target.value)}
                style={styles.input}
                placeholder="e.g., LCSW, LPC, PhD"
              />
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Title:</label>
              <input
                type="text"
                value={therapistTitle}
                onChange={(e) => setTherapistTitle(e.target.value)}
                style={styles.input}
                placeholder="e.g., Therapist, Counselor"
              />
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Phone:</label>
              <input
                type="text"
                value={therapistPhone}
                onChange={(e) => setTherapistPhone(e.target.value)}
                style={styles.input}
                placeholder="Phone number"
              />
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Extension (optional):</label>
              <input
                type="text"
                value={therapistPhoneExtension}
                onChange={(e) => setTherapistPhoneExtension(e.target.value)}
                style={styles.input}
                placeholder="e.g., 123"
              />
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Email:</label>
              <input
                type="email"
                value={therapistEmail}
                onChange={(e) => setTherapistEmail(e.target.value)}
                style={styles.input}
                placeholder="Email address"
              />
            </div>
          </div>
          
          {/* NOTE CONTENT */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Note Content:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Use Custom Content:</label>
              <input
                type="checkbox"
                checked={customContent}
                onChange={(e) => setCustomContent(e.target.checked)}
              />
            </div>
            
            {!customContent ? (
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Additional Notes:</label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  style={styles.textArea}
                  rows={4}
                  placeholder="Any additional information to include..."
                />
                
                <div style={styles.previewBox}>
                  <p><strong>Standard Note Preview:</strong></p>
                  <p>This letter is to document {clientName}'s presence at Avenues Unlimited Counseling Center for {appointmentTime ? `their ${appointmentTime}` : "their"} session on {getDayOfWeek(appointmentDate)} {formatDate(appointmentDate)}.</p>
                  {additionalNotes && <p>{additionalNotes}</p>}
                </div>
              </div>
            ) : (
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Custom Note Content:</label>
                <textarea
                  value={customNoteContent}
                  onChange={(e) => setCustomNoteContent(e.target.value)}
                  style={styles.textArea}
                  rows={6}
                  placeholder="Enter your custom note content..."
                />
              </div>
            )}
          </div>
          
          {/* SIGNATURE */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Therapist Signature:</h3>
            
            <div style={styles.fieldGroup}>
              <div style={styles.signatureBox}>
                <SignatureCanvas 
                  ref={signatureRef}
                  penColor="black"
                  canvasProps={{
                    width: 450,
                    height: 150,
                    className: "signature-canvas"
                  }}
                  onEnd={updateSignature}
                />
              </div>
              <div style={styles.signatureButtons}>
                <button 
                  type="button"
                  onClick={clearSignature}
                  style={styles.secondaryButton}
                >
                  Clear Signature
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.modalButtons}>
          <button onClick={previewNote} style={styles.button}>
            Preview Note
          </button>
          <button onClick={handleSave} style={styles.saveBtn}>
            Save School Note
          </button>
          <button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===============================
   MODAL: VIEW/EDIT SCHOOL NOTE
   =============================== */
function SchoolNoteViewModal({ note, isOpen, onClose, onUpdate, onSendAsPDF }) {
  // Parse the note data - handle the case where note might be null
  // This must be done with useMemo to avoid re-parsing on every render
  const parsedNote = React.useMemo(() => {
    if (!note || !note.notes) return {};
    try {
      return typeof note.notes === "string" ? JSON.parse(note.notes) : note.notes;
    } catch (e) {
      console.error("Error parsing school note:", e);
      return {};
    }
  }, [note]);

  // All hooks must be called unconditionally (before any early returns)
  // This is required by React's Rules of Hooks
  const [editMode, setEditMode] = useState(false);
  const [date, setDate] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientDob, setClientDob] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [therapistName, setTherapistName] = useState("");
  const [therapistCredentials, setTherapistCredentials] = useState("");
  const [therapistTitle, setTherapistTitle] = useState("Therapist");
  const [therapistPhone, setTherapistPhone] = useState("");
  const [therapistPhoneExtension, setTherapistPhoneExtension] = useState("");
  const [therapistEmail, setTherapistEmail] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [customContent, setCustomContent] = useState(false);
  const [customNoteContent, setCustomNoteContent] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  
  const signatureRef = useRef(null);

  // Initialize/update state from parsed note when the note changes
  useEffect(() => {
    if (parsedNote && Object.keys(parsedNote).length > 0) {
      setDate(parsedNote.date || "");
      setClientName(parsedNote.clientName || "");
      setClientDob(parsedNote.clientDob || "");
      setAppointmentTime(parsedNote.appointmentTime || "");
      setAppointmentDate(parsedNote.appointmentDate || "");
      setTherapistName(parsedNote.therapistName || "");
      setTherapistCredentials(parsedNote.therapistCredentials || "");
      setTherapistTitle(parsedNote.therapistTitle || "Therapist");
      setTherapistPhone(parsedNote.therapistPhone || "");
      setTherapistPhoneExtension(parsedNote.therapistPhoneExtension || "");
      setTherapistEmail(parsedNote.therapistEmail || "");
      setAdditionalNotes(parsedNote.additionalNotes || "");
      setCustomContent(parsedNote.customContent || false);
      setCustomNoteContent(parsedNote.customNoteContent || "");
      setSignatureData(parsedNote.signatureData || null);
      setEditMode(false); // Reset edit mode when note changes
    }
  }, [parsedNote]);

  // Early return AFTER all hooks have been called (required by React)
  if (!isOpen || !note) return null;

  const updateSignature = () => {
    try {
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        setSignatureData(signatureRef.current.toDataURL());
      } else {
        setSignatureData(null);
      }
    } catch (error) {
      console.error('Error updating signature:', error);
    }
  };
  
  const clearSignature = () => {
    try {
      if (signatureRef.current) {
        signatureRef.current.clear();
        setSignatureData(null);
      }
    } catch (error) {
      console.error('Error clearing signature:', error);
    }
  };

  // Format date for display (MM/DD/YYYY)
  // Handles both YYYY-MM-DD and ISO timestamp formats
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    
    try {
      // If it's an ISO timestamp (contains 'T'), extract just the date part
      let dateOnly = dateStr;
      if (dateStr.includes('T')) {
        dateOnly = dateStr.split('T')[0];
      }
      
      // Now split the YYYY-MM-DD format
      const parts = dateOnly.split("-");
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
      
      // Fallback: try to parse as Date object
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      }
      
      return dateStr;
    } catch (error) {
      return dateStr;
    }
  };
  
  // Get day of week - FIXED to handle timezone correctly
  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return "";
    
    try {
      // Split the date string to get year, month, and day
      const parts = dateStr.split("-");
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed in JavaScript
      const day = parseInt(parts[2], 10);
      
      // Create date using local timezone instead of UTC
      const date = new Date(year, month, day);
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return days[date.getDay()];
    } catch (error) {
      console.error("Error getting day of week:", error);
      return "";
    }
  };

  const handleSave = () => {
    if (!signatureData) {
      alert("Please sign the note before saving");
      return;
    }
    
    const updatedNote = {
      type: "SchoolNote",
      date,
      clientName,
      clientDob,
      appointmentTime,
      appointmentDate,
      appointmentDayOfWeek: getDayOfWeek(appointmentDate),
      therapistName,
      therapistCredentials,
      therapistTitle,
      therapistPhone,
      therapistPhoneExtension,
      therapistEmail,
      additionalNotes,
      customContent,
      customNoteContent,
      signatureData
    };
    
    onUpdate(note.id, updatedNote);
    setEditMode(false);
  };

  const handlePrint = () => {
    // Create a print window with the school note letterhead format
    const printWin = window.open("", "_blank", "width=800,height=600");
    if (!printWin) {
      alert("Unable to open print window. Please check your popup blocker settings.");
      return;
    }
    
    // Get standard or custom content
    let noteContent;
    if (customContent) {
      noteContent = customNoteContent;
    } else {
      noteContent = `This letter is to document ${clientName}'s presence at Avenues Unlimited Counseling Center for ${appointmentTime ? `their ${appointmentTime}` : "their"} session on ${getDayOfWeek(appointmentDate)} ${formatDate(appointmentDate)}.${additionalNotes ? `\n\n${additionalNotes}` : ""}`;
    }
    
    // Write the print HTML with letterhead
    printWin.document.write(`
      <html>
      <head>
        <title>School Note - ${clientName}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.5;
            margin: 40px;
          }
          .letterhead-container {
            text-align: center;
            margin-bottom: 20px;
          }
          .letterhead {
            max-width: 100%;
            height: auto;
          }
          .footer-container {
            text-align: center;
            margin-top: 30px;
          }
          .footer {
            max-width: 100%;
            height: auto;
          }
          .date {
            margin-bottom: 20px;
          }
          .regarding {
            margin-bottom: 30px;
          }
          .content {
            margin-bottom: 30px;
            white-space: pre-line;
          }
          .signature-area {
            margin-top: 40px;
          }
          .signature {
            max-width: 200px;
            margin-bottom: 10px;
          }
          .closing {
            margin-bottom: 10px;
          }
          .contact {
            margin-top: 5px;
            font-size: 0.9em;
          }
          @media print {
            body {
              margin: 20px;
            }
            @page {
              margin: 0.5cm;
            }
          }
        </style>
      </head>
      <body>
        <div class="letterhead-container">
          <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" class="letterhead">
        </div>
        
        <div class="date">${formatDate(date)}</div>
        
        <div class="regarding">
          <p>Re: ${clientName}</p>
          <p>DOB: ${formatDate(clientDob)}</p>
        </div>
        
        <p>To whom it may concern,</p>
        
        <div class="content">
          ${noteContent}
        </div>
        
        <p>If further information is needed, please contact me directly using the information below.</p>
        
        <div class="closing">Sincerely,</div>
        
        <div class="signature-area">
          ${signatureData ? `<img src="${signatureData}" class="signature" alt="Therapist Signature">` : '(No signature)'}
          <p>${therapistName}${therapistCredentials ? `, ${therapistCredentials}` : ""}</p>
          <p>${therapistTitle}</p>
          <p class="contact">p. ${therapistPhone}${therapistPhoneExtension ? ` ext. ${therapistPhoneExtension}` : ""}</p>
          <p class="contact">e. ${therapistEmail}</p>
        </div>
        
        <div class="footer-container">
          <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" class="footer">
        </div>
      </body>
      </html>
    `);
    
    printWin.document.close();
    
    // Wait for images to load before printing
    printWin.onload = function() {
      printWin.focus();
      printWin.print();
    };
  };

  const renderField = (value, setter, type = "text") => {
    if (!editMode) {
      if (type === "checkbox") {
        return <div style={styles.readOnly}>{value ? "Yes" : "No"}</div>;
      }
      if (type === "textarea") {
        return <div style={styles.readOnly}>{value || "(empty)"}</div>;
      }
      return <div style={styles.readOnly}>{value || "(empty)"}</div>;
    }
    
    if (type === "checkbox") {
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => setter(e.target.checked)}
        />
      );
    }
    
    if (type === "textarea") {
      return (
        <textarea
          value={value}
          onChange={(e) => setter(e.target.value)}
          style={styles.textArea}
          rows={4}
        />
      );
    }
    
    if (type === "date") {
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => setter(e.target.value)}
          style={styles.input}
        />
      );
    }
    
    return (
      <input
        type={type}
        value={value}
        onChange={(e) => setter(e.target.value)}
        style={styles.input}
      />
    );
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>
          {editMode ? "Edit School Note" : "View School Note"}
        </h2>
        
        <div style={styles.formScrollArea}>
          {/* Note Date */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Note Date:</h3>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Date:</label>
              {editMode ? (
                renderField(date, setDate, "date")
              ) : (
                <div style={styles.readOnly}>{formatDate(date)}</div>
              )}
            </div>
          </div>
          
          {/* Client Info */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Client Information:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Client Name:</label>
              {renderField(clientName, setClientName)}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Client Date of Birth:</label>
              {editMode ? (
                renderField(clientDob, setClientDob, "date")
              ) : (
                <div style={styles.readOnly}>{formatDate(clientDob)}</div>
              )}
            </div>
          </div>
          
          {/* Appointment Details */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Appointment Details:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Appointment Date:</label>
              {editMode ? (
                <>
                  {renderField(appointmentDate, setAppointmentDate, "date")}
                  {appointmentDate && (
                    <div style={styles.helperText}>
                      Day of week: {getDayOfWeek(appointmentDate)}
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.readOnly}>
                  {getDayOfWeek(appointmentDate)} {formatDate(appointmentDate)}
                </div>
              )}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Appointment Time/Type:</label>
              {renderField(appointmentTime, setAppointmentTime)}
            </div>
          </div>
          
          {/* Therapist Info */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Therapist Information:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Therapist Name:</label>
              {renderField(therapistName, setTherapistName)}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Credentials:</label>
              {renderField(therapistCredentials, setTherapistCredentials)}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Title:</label>
              {renderField(therapistTitle, setTherapistTitle)}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Phone:</label>
              {renderField(therapistPhone, setTherapistPhone)}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Extension:</label>
              {renderField(therapistPhoneExtension, setTherapistPhoneExtension)}
            </div>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Email:</label>
              {renderField(therapistEmail, setTherapistEmail)}
            </div>
          </div>
          
          {/* NOTE CONTENT */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Note Content:</h3>
            
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Use Custom Content:</label>
              {renderField(customContent, setCustomContent, "checkbox")}
            </div>
            
            {!customContent ? (
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Additional Notes:</label>
                {renderField(additionalNotes, setAdditionalNotes, "textarea")}
                
                {!editMode && (
                  <div style={styles.previewBox}>
                    <p><strong>Standard Note Preview:</strong></p>
                    <p>This letter is to document {clientName}'s presence at Avenues Unlimited Counseling Center for {appointmentTime ? `their ${appointmentTime}` : "their"} session on {getDayOfWeek(appointmentDate)} {formatDate(appointmentDate)}.</p>
                    {additionalNotes && <p>{additionalNotes}</p>}
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Custom Note Content:</label>
                {renderField(customNoteContent, setCustomNoteContent, "textarea")}
              </div>
            )}
          </div>
          
          {/* SIGNATURE */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Therapist Signature:</h3>
            
            {editMode ? (
              <div style={styles.fieldGroup}>
                <div style={styles.signatureBox}>
                  <SignatureCanvas 
                    ref={signatureRef}
                    penColor="black"
                    canvasProps={{
                      width: 450,
                      height: 150,
                      className: "signature-canvas"
                    }}
                    onEnd={updateSignature}
                  />
                </div>
                <div style={styles.signatureButtons}>
                  <button 
                    type="button"
                    onClick={clearSignature}
                    style={styles.secondaryButton}
                  >
                    Clear Signature
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.fieldGroup}>
                {signatureData ? (
                  <img 
                    src={signatureData} 
                    alt="Therapist Signature" 
                    style={{ maxWidth: "200px", maxHeight: "100px" }} 
                  />
                ) : (
                  <div style={styles.readOnly}>(No signature)</div>
                )}
              </div>
            )}
          </div>
        </div>

        {editMode && (
          <div style={styles.modalButtons}>
            <button onClick={handleSave} style={styles.saveBtn}>
              Save Changes
            </button>
            <button 
              onClick={() => setEditMode(false)} 
              style={styles.cancelBtn}
            >
              Cancel Edits
            </button>
          </div>
        )}

        {!editMode && (
          <div style={styles.modalButtons}>
            <button onClick={() => setEditMode(true)} style={styles.button}>
              Edit Note
            </button>
            <button onClick={handlePrint} style={styles.button}>
              Print/Export PDF
            </button>
            {onSendAsPDF && (
              <button onClick={() => onSendAsPDF(note)} style={{ ...styles.button, backgroundColor: "var(--primary-color)" }}>
                ✉️ Send as PDF
              </button>
            )}
            <button onClick={onClose} style={styles.cancelBtn}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles for the modals
const styles = {
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "1rem"
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "6px",
    maxHeight: "90vh",
    overflowY: "auto",
    width: "700px",
    maxWidth: "94vw",
    boxSizing: "border-box",
    position: "relative"
  },
  modalHeader: {
    fontSize: "1.25rem",
    margin: 0,
    marginBottom: "0.5rem"
  },
  clientInfo: {
    marginBottom: "1rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "1rem"
  },
  formScrollArea: {
    maxHeight: "60vh",
    overflowY: "auto",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px"
  },
  formSection: {
    marginBottom: "1.5rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    border: "1px solid var(--border-color)"
  },
  fieldGroup: {
    marginBottom: "1rem"
  },
  sectionHeader: {
    fontSize: "1rem",
    fontWeight: "bold",
    margin: "0 0 0.75rem 0",
    color: "var(--text-strong)",
    borderBottom: "1px solid var(--border-color)",
    paddingBottom: "0.25rem"
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: "0.5rem",
    display: "block"
  },
  textArea: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    resize: "vertical",
    fontFamily: "inherit",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  smallButton: {
    padding: "0.25rem 0.5rem",
    fontSize: "0.8rem",
    backgroundColor: "var(--text-muted)",
    color: "#fff",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dateInput: {
    padding: "0.25rem",
    border: "1px solid var(--border-color)",
    borderRadius: "3px",
    marginLeft: "0.5rem"
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem 0"
  },
  signatureBox: {
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    marginBottom: "0.5rem",
    backgroundColor: "#fff",
    overflow: "hidden"
  },
  signatureButtons: {
    display: "flex",
    gap: "0.5rem"
  },
  secondaryButton: {
    backgroundColor: "var(--surface-muted)",
    border: "1px solid var(--border-color)",
    padding: "0.25rem 0.5rem",
    borderRadius: "3px",
    cursor: "pointer"
  },
  previewBox: {
    backgroundColor: "var(--surface-muted)",
    padding: "1rem",
    borderRadius: "4px",
    marginTop: "1rem",
    border: "1px solid var(--border-color)",
    fontSize: "0.9rem"
  },
  helperText: {
    fontSize: "0.8rem",
    color: "var(--text-muted)",
    marginTop: "0.25rem"
  },
  readOnly: {
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    minHeight: "1rem"
  },
  formField: {
    marginBottom: "0.75rem"
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    marginTop: "1rem"
  },
  saveBtn: {
    backgroundColor: "var(--success-color)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  cancelBtn: {
    backgroundColor: "var(--text-strong)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  button: {
    backgroundColor: "var(--primary-color)",
    border: "none",
    color: "#fff",
    padding: "0.4rem 0.75rem",
    borderRadius: "4px",
    cursor: "pointer"
  }
};

export { SchoolNoteModal, SchoolNoteViewModal };
